/* eslint-disable @typescript-eslint/no-explicit-any */
import { GetServerSideProps } from 'next';

import { PageRendererComponent, getProcessEnvs, prePageCall, canUseDOM } from '@marriott/mi-headless-utils';
import { fetchCFData, buildPatternSlots, mergeCfAndDcaToSlots } from '@marriott/mi-homepage-components';
import { FEATURE_FLAGS, fetchFlag, TARGETS } from '@marriott/mi-feature-flag-utils';
import { envVarKeys } from '../_constants/envVarKeys';
import operationSignatures from '../_constants/operationSignatures.json';
import homepageMockData from '../mock/polaris-logged-out.json';

if (canUseDOM) window.$ = window.jQuery = require('jquery-slim/dist/jquery.slim.min.js');

export default function pageRenderer(props: any) {
  return <PageRendererComponent {...props} />;
}

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
const homepageSpecificData = async () => {
  const {
    NEXT_PUBLIC_SUBMIT_SEARCH_URL,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    GOOGLE_MAP_API_KEY,
    BAIDU_MAP_API_KEY = null,
    UXL_IMAGE_CACHE_DOMAIN = null,
    SESSION_GET_CALL_URL_CLIENT,
    SESSION_APP_GET_CALL_V1_URL,
    DEPLOYED_ENV_CONFIG,
    TARGET_PROPERTY_TOKEN,
    NEXT_PUBLIC_FETCH_OFFERS_API_URL,
    SESSION_APP_POST_CALL_URL,
    INJECT_BRAND_CSS,
    NEXT_PUBLIC_PREFIX,
    GET_DESTINATION_TYPE_FLAG,
  } = getProcessEnvs();

  // Feature flag call for logo
  const featureFlagLogo = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_LOGO_AUTOMATION, {
    target: TARGETS.APP.HOMEPAGE_RENDERER,
  });
  // Feature flag call to get cardCarousel flag
  const featureFlags = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_CAROUSEL_LIGHTINING_VARIANTS, {
    target: TARGETS.APP.HOMEPAGE_RENDERER,
  });
  // Feature flag to control deferred hydration optimization
  const featureFlagDeferHydration = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_HOMEPAGE_DEFER_HYDRATION, {
    target: TARGETS.APP.HOMEPAGE_RENDERER,
  });

  return {
    operationSignatures,
    productSpecificResponse: [],
    NEXT_PUBLIC_SUBMIT_SEARCH_URL: NEXT_PUBLIC_SUBMIT_SEARCH_URL ?? null,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL: NEXT_PUBLIC_AVAILABILITY_SEARCH_URL ?? null,
    // Next.js cannot serialize `undefined` in props; ensure we pass null when unset.
    NEXT_PUBLIC_FETCH_OFFERS_API_URL: NEXT_PUBLIC_FETCH_OFFERS_API_URL ?? null,
    TARGET_PROPERTY_TOKEN: TARGET_PROPERTY_TOKEN ?? null,
    GOOGLE_MAP_API_KEY: GOOGLE_MAP_API_KEY ?? null,
    BAIDU_MAP_API_KEY,
    UXL_IMAGE_CACHE_DOMAIN,
    SESSION_GET_CALL_URL_CLIENT: SESSION_GET_CALL_URL_CLIENT ?? null,
    SESSION_APP_GET_CALL_V1_URL: SESSION_APP_GET_CALL_V1_URL ?? null,
    SESSION_APP_POST_CALL_URL: SESSION_APP_POST_CALL_URL ?? null,
    DEPLOYED_ENV_CONFIG: DEPLOYED_ENV_CONFIG ?? null,
    INJECT_BRAND_CSS: INJECT_BRAND_CSS ?? null,
    buildInfo: {
      deployedEnvType: process.env.DEPLOYED_ENV_TYPE ?? null,
      deployedEnvConfig: process.env.DEPLOYED_ENV_CONFIG ?? null,
      jenkinsDeploymentBuild: process.env.JENKINS_DEPLOYMENT_BUILD ?? null,
    },
    featureFlagLogo: featureFlagLogo ?? null,
    featureFlagDeferHydration: featureFlagDeferHydration ?? null,
    NEXT_PUBLIC_PREFIX: NEXT_PUBLIC_PREFIX ?? null,
    featureFlags: featureFlags ?? null,
    nextPublicEnvKeys: [...Object.keys(envVarKeys)],
    GET_DESTINATION_TYPE_FLAG: GET_DESTINATION_TYPE_FLAG ?? null,
  };
};

const resolveUserState = (_req: any, parallelCallsResponse: any) => {
  const sessionState = parallelCallsResponse?.sessionData;
  const { authenticated } = sessionState;
  return authenticated;
};

/**
 * Ensure SSR props are JSON-safe for Next.js by replacing nested `undefined`
 * values with `null`.
 */
const sanitizeForSsr = <T,>(value: T): T =>
  JSON.parse(JSON.stringify(value, (_key, currentValue) => (currentValue === undefined ? null : currentValue))) as T;

/**
 * This function is called on the server side before the page is rendered.
 * @param _context - The context object containing the request and response objects.
 * @param _parallelCallsResponse - The parallel calls response object.
 * @param _interceptorResponse - The interceptor response object.
 * @param model - The model object containing the page data.
 * @returns The updated model object.
 */
const postAEMModelOperations = async (
  context: any,
  parallelCallsResponse: any,
  _interceptorResponse: any,
  model: any
) => {
  try {
    console.log('Server Side: postAEMModelOperations started');
    const gridItems = model?.cqItems?.root?.[':items']?.responsivegrid?.[':items'] || {};

    const tl = gridItems?.tabbedlayout || gridItems?.dynamictabbedlayout;
    const isNorthStarPage = !!tl && (typeof tl === 'object' ? true : !!tl);
    model.isNorthStarPage = isNorthStarPage;

    // TODO: This is as of now done for Polaris POC work. It can be better optimized in the future.
    const dynamictabbedlayoutComp = gridItems['dynamictabbedlayout'];
    if (dynamictabbedlayoutComp) {
      const tabDetails: any[] = dynamictabbedlayoutComp.tabDetails || [];
      if (!tabDetails.length) return model;

      const queryTab: string | undefined = context?.query?.tab as string;
      const firstTabName: string = tabDetails[0]?.tabName || '';

      // Determine the requested tab - use first tab as fallback when missing
      let requestedTab: string = queryTab || firstTabName;

      // Validate the requested tab exists in the authored tab list
      let chosenTab = tabDetails.find(t => (t.tabName || '') === requestedTab);

      // If an invalid tab was explicitly provided via query param, fall back to first tab.
      // We render the first tab's content server-side; the client-side hydration effect
      // in DynamicTabbedLayout corrects the URL via replaceState.
      // Note: Server-side redirect is not viable because the nginx proxy rewrites the URL
      // (strips /default/ prefix and .mi extension) before it reaches Next.js, so
      // context.resolvedUrl doesn't contain the browser-facing path.
      if (queryTab && !chosenTab) {
        requestedTab = firstTabName;
        chosenTab = tabDetails[0];
      }

      if (!chosenTab) return model;

      const bannerFirst = Boolean(chosenTab.bannerFirst);
      const squareFirst = chosenTab.squareFirst !== false;
      const dcaTiles = chosenTab.DCAtiles || [];
      const slotsMeta = buildPatternSlots(bannerFirst, squareFirst, dcaTiles);
      const userState = resolveUserState(context?.req, parallelCallsResponse);
      const cf = await fetchCFData(chosenTab.tabName, false, userState);
      const mergedSlots = mergeCfAndDcaToSlots(slotsMeta, dcaTiles, cf);
      // Inject SSR seed so the chosen tab renders immediately on server
      dynamictabbedlayoutComp.requestedTabData = sanitizeForSsr(mergedSlots);
      dynamictabbedlayoutComp.requestedTab = requestedTab;
    }
  } catch {
    //silent failure
  }
  return model;
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
export const getServerSideProps: GetServerSideProps<any> = prePageCall(
  homepageSpecificData,
  homepageMockData || {},
  false,
  false,
  false,
  undefined,
  postAEMModelOperations
);
